import {
  buildMapExperienceTargetKey,
  normalizeOptionalMapExperienceColorValue,
  resolveMapExperienceColorValue,
  resolveMapExperienceRenderStackEntries,
  type MapExperienceMetadataV1,
  type MapExperienceRenderStackRole,
  type MapExperienceStyleColorValue,
  type MapExperienceStyleProfile,
  type MapExperienceStyleProfileKind,
  type MapExperienceStyleRepresentation,
} from '../map-experience';
import type { StyleRuleLogic } from '../map-style-workbench/builderLogic';
import type { SemanticThemeColorPalette } from '../../theme';

export type MapWorkbenchRenderStackTemplateId =
  | 'badge'
  | 'label'
  | 'symbol_overlay'
  | 'duplicate_base_as_overlay';

export type MapWorkbenchRenderStackAddRequestId =
  | MapWorkbenchRenderStackTemplateId
  | 'overlay_badge'
  | 'overlay_label'
  | 'overlay_symbol';

export type MapWorkbenchRenderStackGeometryKind = 'point' | 'line' | 'polygon' | 'unknown';

export type MapWorkbenchRenderStackEditorSupportState = 'available' | 'not_implemented';

export interface MapWorkbenchRenderStackTemplateOption {
  id: MapWorkbenchRenderStackTemplateId;
  label: string;
  description: string;
  stack_role: Exclude<MapExperienceRenderStackRole, 'base'>;
}

export interface MapWorkbenchRenderStackEditorRepresentationOption {
  value: MapExperienceStyleRepresentation;
  label: string;
  state: MapWorkbenchRenderStackEditorSupportState;
  helperText?: string | null;
}

export interface MapWorkbenchRenderStackEditorStrategyOption {
  value: MapExperienceStyleProfileKind;
  label: string;
  state: MapWorkbenchRenderStackEditorSupportState;
  description: string;
}

export interface MapWorkbenchRenderStackEditorControlSupport {
  id:
    | 'strategy'
    | 'representation'
    | 'filter'
    | 'legend'
    | 'icon_picker'
    | 'text_binding'
    | 'badge_presets'
    | 'symbol_layout';
  label: string;
  state: MapWorkbenchRenderStackEditorSupportState;
  description: string;
}

export type MapWorkbenchRenderStackSymbolContentMode = 'sprite' | 'glyph';

export interface MapWorkbenchRenderStackLayoutControlOption {
  value: string;
  label: string;
}

export interface MapWorkbenchRenderStackLayoutControlDescriptor {
  target: 'layout' | 'paint';
  id:
    | 'text_anchor'
    | 'text_offset'
    | 'text_radial_offset'
    | 'icon_offset'
    | 'icon_size'
    | 'text_size'
    | 'circle_radius'
    | 'icon_allow_overlap'
    | 'icon_ignore_placement'
    | 'text_allow_overlap'
    | 'text_ignore_placement';
  prop:
    | 'text-anchor'
    | 'text-offset'
    | 'text-radial-offset'
    | 'icon-offset'
    | 'icon-size'
    | 'text-size'
    | 'circle-radius'
    | 'icon-allow-overlap'
    | 'icon-ignore-placement'
    | 'text-allow-overlap'
    | 'text-ignore-placement';
  label: string;
  description: string;
  state: MapWorkbenchRenderStackEditorSupportState;
  input: 'select' | 'number' | 'number-list' | 'boolean';
  current_value?: unknown;
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  options?: MapWorkbenchRenderStackLayoutControlOption[];
}

export interface MapWorkbenchRenderStackTextBindingControlDescriptor {
  id: 'text_field';
  prop: 'text-field';
  label: string;
  description: string;
  state: MapWorkbenchRenderStackEditorSupportState;
  current_value?: string | null;
  placeholder?: string;
}

export type MapWorkbenchRenderStackColorBindingId = 'primary' | 'stroke' | 'halo' | 'icon' | 'icon_halo';

export interface MapWorkbenchRenderStackSemanticColorOption {
  token:
    | 'foreground'
    | 'muted-foreground'
    | 'card-foreground'
    | 'primary'
    | 'primary-foreground'
    | 'secondary'
    | 'secondary-foreground'
    | 'accent'
    | 'accent-foreground'
    | 'destructive'
    | 'destructive-foreground'
    | 'map-label'
    | 'map-border'
    | 'map-background'
    | 'map-road'
    | 'map-water'
    | 'map-landuse';
  label: string;
  description: string;
  swatch_color?: string | null;
}

export interface MapWorkbenchRenderStackColorBindingDescriptor {
  id: MapWorkbenchRenderStackColorBindingId;
  label: string;
  description: string;
  state: MapWorkbenchRenderStackEditorSupportState;
  supports_literal_color: boolean;
  recommended_token?: MapWorkbenchRenderStackSemanticColorOption['token'] | null;
  current_value?: MapExperienceStyleColorValue | null;
  resolved_preview_color?: string | null;
  semantic_options: MapWorkbenchRenderStackSemanticColorOption[];
}

export interface MapWorkbenchRenderStackEntryEditorViewModel {
  entry_id: string;
  profile_id: string;
  stack_role: MapExperienceRenderStackRole;
  role_label: string;
  title: string;
  description: string;
  symbol_content_mode?: MapWorkbenchRenderStackSymbolContentMode | null;
  strategy_options: MapWorkbenchRenderStackEditorStrategyOption[];
  representation_options: MapWorkbenchRenderStackEditorRepresentationOption[];
  control_support: MapWorkbenchRenderStackEditorControlSupport[];
  text_binding_controls: MapWorkbenchRenderStackTextBindingControlDescriptor[];
  color_bindings: MapWorkbenchRenderStackColorBindingDescriptor[];
  layout_controls: MapWorkbenchRenderStackLayoutControlDescriptor[];
}

export interface MapWorkbenchRenderStackEntryViewModel {
  id: string;
  label: string;
  enabled: boolean;
  synthetic: boolean;
  stack_role: MapExperienceRenderStackRole;
  role_label: string;
  profile_id: string;
  profile_kind: MapExperienceStyleProfileKind;
  representation: MapExperienceStyleRepresentation | null;
  swatch_color: string | null;
  filter_logic: StyleRuleLogic | null;
  filter_summary: string | null;
  position: number;
  is_base: boolean;
  can_move_up: boolean;
  can_move_down: boolean;
}

export interface MapWorkbenchRenderStackControllerViewModel {
  target_key: string;
  selected_entry_id: string | null;
  selected_entry: MapWorkbenchRenderStackEntryViewModel | null;
  selected_entry_editor: MapWorkbenchRenderStackEntryEditorViewModel | null;
  read_only: boolean;
  entries: MapWorkbenchRenderStackEntryViewModel[];
  template_options: MapWorkbenchRenderStackTemplateOption[];
  can_add_overlay: boolean;
  onSelectEntry?: (entryId: string) => void;
  onAddOverlay?: (templateId: MapWorkbenchRenderStackTemplateId) => void;
  onMoveEntry?: (entryId: string, direction: 'up' | 'down') => void;
  onDuplicateEntry?: (entryId: string) => void;
  onDeleteEntry?: (entryId: string) => void;
  onToggleEntryEnabled?: (entryId: string, enabled: boolean) => void;
  onEditEntry?: (entryId: string) => void;
}

type BuildMapWorkbenchRenderStackControllerArgs = {
  experience: MapExperienceMetadataV1;
  target: {
    sourceId: string;
    sourceLayer: string;
    geometry?: MapWorkbenchRenderStackGeometryKind;
  };
  spriteIconSdfByToken?: Record<string, boolean> | null;
  themeColors?: SemanticThemeColorPalette | null;
  readOnly?: boolean;
  selectedEntryId?: string | null;
  selectedProfileId?: string | null;
  onSelectEntry?: (entryId: string) => void;
  onAddOverlay?: (templateId: MapWorkbenchRenderStackTemplateId) => void;
  onMoveEntry?: (entryId: string, direction: 'up' | 'down') => void;
  onDuplicateEntry?: (entryId: string) => void;
  onDeleteEntry?: (entryId: string) => void;
  onToggleEntryEnabled?: (entryId: string, enabled: boolean) => void;
  onEditEntry?: (entryId: string) => void;
};

const RENDER_STACK_TEMPLATE_OPTIONS: MapWorkbenchRenderStackTemplateOption[] = [
  {
    id: 'badge',
    label: 'Badge',
    description: 'Add a compact overlay badge above the base style.',
    stack_role: 'overlay_badge',
  },
  {
    id: 'label',
    label: 'Label',
    description: 'Add a text label overlay with its own filter.',
    stack_role: 'overlay_label',
  },
  {
    id: 'symbol_overlay',
    label: 'Symbol Overlay',
    description: 'Add a second marker or icon layer above the base entry.',
    stack_role: 'overlay_symbol',
  },
  {
    id: 'duplicate_base_as_overlay',
    label: 'Duplicate Base as Overlay',
    description: 'Start a new overlay from the base entry and narrow it with a filter.',
    stack_role: 'overlay_symbol',
  },
];

const STACK_ROLE_LABELS: Record<MapExperienceRenderStackRole, string> = {
  base: 'Base Style',
  overlay_badge: 'Badge Overlay',
  overlay_label: 'Label Overlay',
  overlay_symbol: 'Symbol Overlay',
};

const SEMANTIC_COLOR_OPTION_DEFINITIONS: Array<{
  token: MapWorkbenchRenderStackSemanticColorOption['token'];
  label: string;
  description: string;
}> = [
  { token: 'primary', label: 'Primary', description: 'Accent color for authored map treatments.' },
  { token: 'primary-foreground', label: 'Primary Foreground', description: 'High-contrast foreground for primary accents.' },
  { token: 'foreground', label: 'Foreground', description: 'Default shell foreground token.' },
  { token: 'muted-foreground', label: 'Muted Foreground', description: 'Subdued foreground tone for secondary marks.' },
  { token: 'card-foreground', label: 'Card Foreground', description: 'Readable foreground used in card surfaces.' },
  { token: 'secondary', label: 'Secondary', description: 'Secondary shell accent token.' },
  { token: 'secondary-foreground', label: 'Secondary Foreground', description: 'Readable foreground on secondary accents.' },
  { token: 'accent', label: 'Accent', description: 'Alternative accent token for emphasis.' },
  { token: 'accent-foreground', label: 'Accent Foreground', description: 'Readable foreground on accent colors.' },
  { token: 'destructive', label: 'Destructive', description: 'Attention-grabbing destructive accent token.' },
  { token: 'destructive-foreground', label: 'Destructive Foreground', description: 'Readable foreground on destructive accents.' },
  { token: 'map-label', label: 'Map Label', description: 'Map-native label contrast color for the active basemap theme.' },
  { token: 'map-border', label: 'Map Border', description: 'Map-native border and outline contrast token.' },
  { token: 'map-background', label: 'Map Background', description: 'Map-native background token for halos and contrast fills.' },
  { token: 'map-road', label: 'Map Road', description: 'Map-native road color token.' },
  { token: 'map-water', label: 'Map Water', description: 'Map-native water color token.' },
  { token: 'map-landuse', label: 'Map Landuse', description: 'Map-native landuse color token.' },
];

const ALL_REPRESENTATION_OPTIONS: MapWorkbenchRenderStackEditorRepresentationOption[] = [
  { value: 'fill', label: 'Polygon Fill', state: 'available' },
  { value: 'line', label: 'Line / Path', state: 'available' },
  { value: 'circle', label: 'Point Circle', state: 'available' },
  { value: 'symbol', label: 'Symbol / Label', state: 'available' },
  { value: 'fill-extrusion', label: '3D Extrusion', state: 'available' },
];

const TEXT_ANCHOR_OPTIONS: MapWorkbenchRenderStackLayoutControlOption[] = [
  { value: 'center', label: 'Center' },
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'top', label: 'Top' },
  { value: 'bottom', label: 'Bottom' },
  { value: 'top-left', label: 'Top Left' },
  { value: 'top-right', label: 'Top Right' },
  { value: 'bottom-left', label: 'Bottom Left' },
  { value: 'bottom-right', label: 'Bottom Right' },
];

const getRoleAwareStrategyOptions = (
  stackRole: MapExperienceRenderStackRole,
): MapWorkbenchRenderStackEditorStrategyOption[] => {
  const baseOption: MapWorkbenchRenderStackEditorStrategyOption = {
    value: 'rules',
    label: 'Rule Based',
    state: 'available',
    description: 'Author ordered rules for conditional styling inside this render-stack entry.',
  };

  if (stackRole === 'overlay_label') {
    return [{ ...baseOption, description: 'Author ordered rules for conditional label logic and text styling.' }];
  }

  if (stackRole === 'overlay_badge') {
    return [{ ...baseOption, description: 'Author ordered rules for conditional badge visibility and styling.' }];
  }

  if (stackRole === 'overlay_symbol') {
    return [{ ...baseOption, description: 'Author ordered rules for conditional symbol overlays.' }];
  }

  return [baseOption];
};

const getGeometryRepresentationOptions = (
  geometry: MapWorkbenchRenderStackGeometryKind,
): MapWorkbenchRenderStackEditorRepresentationOption[] => {
  switch (geometry) {
    case 'point':
      return ALL_REPRESENTATION_OPTIONS.filter(option => option.value === 'circle' || option.value === 'symbol');
    case 'line':
      return ALL_REPRESENTATION_OPTIONS.filter(option => option.value === 'line');
    case 'polygon':
      return ALL_REPRESENTATION_OPTIONS.filter(
        option => option.value === 'fill' || option.value === 'line' || option.value === 'fill-extrusion',
      );
    default:
      return ALL_REPRESENTATION_OPTIONS;
  }
};

const getRoleAwareRepresentationOptions = (
  stackRole: MapExperienceRenderStackRole,
  geometry: MapWorkbenchRenderStackGeometryKind,
  currentRepresentation: MapExperienceStyleRepresentation | null,
): MapWorkbenchRenderStackEditorRepresentationOption[] => {
  const geometryOptions = getGeometryRepresentationOptions(geometry);
  const roleScopedOptions =
    stackRole === 'overlay_label'
      ? geometryOptions
          .filter(option => option.value === 'symbol')
          .map(option => ({
            ...option,
            helperText: 'Label overlays currently render through symbol text controls.',
          }))
      : stackRole === 'overlay_badge'
        ? geometryOptions
            .filter(option => option.value === 'circle' || option.value === 'symbol')
            .map(option => ({
              ...option,
              helperText:
                option.value === 'circle'
                  ? 'Circle badges work well for compact status dots and count chips.'
                  : 'Symbol badges support text or icon-based status marks.',
            }))
        : stackRole === 'overlay_symbol'
          ? geometryOptions
              .filter(option => option.value === 'symbol' || option.value === 'circle')
              .map(option => ({
                ...option,
                helperText:
                  option.value === 'symbol'
                    ? 'Use symbol overlays for icon and text-first adornments.'
                    : 'Circle overlays remain available for simple marker duplication on point targets.',
              }))
          : geometryOptions;
  const nextOptions =
    roleScopedOptions.length > 0
      ? [...roleScopedOptions]
      : [...geometryOptions];
  if (currentRepresentation && !nextOptions.some(option => option.value === currentRepresentation)) {
    const fallbackOption = ALL_REPRESENTATION_OPTIONS.find(option => option.value === currentRepresentation);
    if (fallbackOption) {
      nextOptions.push({
        ...fallbackOption,
        helperText: 'This representation remains available to preserve the authored entry until dedicated role-specific editing lands.',
      });
    }
  }
  return nextOptions;
};

const getRoleAwareControlSupport = (
  stackRole: MapExperienceRenderStackRole,
  options?: {
    targetHasBadgeOverlay?: boolean;
    representation?: MapExperienceStyleRepresentation | null;
  },
): MapWorkbenchRenderStackEntryEditorViewModel['control_support'] => {
  switch (stackRole) {
    case 'overlay_badge':
      return [
        {
          id: 'strategy',
          label: 'Entry Strategy',
          state: 'available',
          description: 'Rules are the standard authoring model for this badge overlay entry.',
        },
        {
          id: 'representation',
          label: 'Badge Representation',
          state: 'available',
          description: 'Badge overlays currently support compact circle and symbol treatments.',
        },
        {
          id: 'filter',
          label: 'Overlay Filter',
          state: 'available',
          description: 'Use a narrower filter so the badge decorates only matching features.',
        },
        {
          id: 'symbol_layout',
          label: 'Badge Layout',
          state: 'available',
          description: 'Badge layout controls handle anchored sizing, offset, and collision behavior.',
        },
        {
          id: 'badge_presets',
          label: 'Badge Presets',
          state: 'not_implemented',
          description: 'Dedicated badge shortcut controls are planned but not wired into the shared editor yet.',
        },
      ];
    case 'overlay_label':
      return [
        {
          id: 'strategy',
          label: 'Entry Strategy',
          state: 'available',
          description: 'Rules are the standard authoring model for this label overlay entry.',
        },
        {
          id: 'representation',
          label: 'Label Representation',
          state: 'available',
          description: 'Label overlays are constrained to symbol-based rendering in the current shared editor.',
        },
        {
          id: 'text_binding',
          label: 'Label Text Binding',
          state: 'available',
          description: 'Use symbol text-field controls to bind a field or authored text.',
        },
        {
          id: 'symbol_layout',
          label: 'Label Layout',
          state: 'available',
          description: 'Shared symbol layout controls handle text size, overlap, and placement behavior.',
        },
        {
          id: 'icon_picker',
          label: 'Icon Picker',
          state: 'not_implemented',
          description: 'A label-first icon toggle contract is planned, but the dedicated label surface is not implemented yet.',
        },
      ];
    case 'overlay_symbol':
      return [
        {
          id: 'strategy',
          label: 'Entry Strategy',
          state: 'available',
          description: 'Rules are the standard authoring model for this symbol overlay entry.',
        },
        {
          id: 'representation',
          label: 'Overlay Representation',
          state: 'available',
          description: 'Symbol overlays currently support symbol-first rendering, with circle duplication available on point targets.',
        },
        {
          id: 'icon_picker',
          label: 'Icon Picker',
          state: 'available',
          description: 'Use the symbol icon picker to select sprite-backed marker adornments.',
        },
        {
          id: 'symbol_layout',
          label: 'Symbol Layout',
          state: 'available',
          description: 'Shared symbol layout controls handle icon sizing, text, and placement behavior.',
        },
      ];
    case 'base':
    default:
      return [
        {
          id: 'strategy',
          label: 'Entry Strategy',
          state: 'available',
          description: 'Rules are the standard authoring model for this base entry.',
        },
        {
          id: 'representation',
          label: 'Representation',
          state: 'available',
          description: 'Base entries can use any geometry-safe representation.',
        },
        {
          id: 'legend',
          label: 'Legend Settings',
          state: 'available',
          description: 'Legend labeling and visibility are available on the selected rules entry.',
        },
        {
          id: 'filter',
          label: 'Entry Filter',
          state: 'available',
          description: 'Base entries may still be narrowed by authored filter logic when needed.',
        },
        ...(options?.representation === 'symbol'
          ? [{
              id: 'symbol_layout' as const,
              label: 'Symbol Layout',
              state: 'available' as const,
              description: options?.targetHasBadgeOverlay
                ? 'Symbol layout controls remain active here because overlap is required while badge overlays exist on this target.'
                : 'Symbol layout controls handle icon/text collision behavior for the base symbol entry.',
            }]
          : []),
      ];
  }
};

const buildSemanticColorOptions = (
  themeColors?: SemanticThemeColorPalette | null,
): MapWorkbenchRenderStackSemanticColorOption[] => SEMANTIC_COLOR_OPTION_DEFINITIONS.map(option => ({
  ...option,
  swatch_color: themeColors?.[option.token] ?? null,
}));

const getPaintPropertyForBinding = (
  bindingId: MapWorkbenchRenderStackColorBindingId,
  representation: MapExperienceStyleRepresentation | null | undefined,
): string | null => {
  const resolvedRepresentation = representation ?? 'fill';
  switch (bindingId) {
    case 'primary':
      switch (resolvedRepresentation) {
        case 'fill':
          return 'fill-color';
        case 'line':
          return 'line-color';
        case 'circle':
          return 'circle-color';
        case 'symbol':
          return 'text-color';
        case 'fill-extrusion':
          return 'fill-extrusion-color';
        default:
          return null;
      }
    case 'stroke':
      switch (resolvedRepresentation) {
        case 'fill':
          return 'fill-outline-color';
        case 'circle':
          return 'circle-stroke-color';
        default:
          return 'stroke-color';
      }
    case 'halo':
      return resolvedRepresentation === 'symbol' ? 'text-halo-color' : 'halo-color';
    case 'icon':
      return 'icon-color';
    case 'icon_halo':
      return 'icon-halo-color';
    default:
      return null;
  }
};

const readSymbolIconToken = (profile: MapExperienceStyleProfile): string | null => {
  const iconToken =
    typeof profile.layout?.['icon-image'] === 'string' && profile.layout['icon-image'].trim().length > 0
      ? profile.layout['icon-image'].trim()
      : null;
  return iconToken;
};

const resolveSymbolContentMode = (
  stackRole: MapExperienceRenderStackRole,
  profile: MapExperienceStyleProfile,
): MapWorkbenchRenderStackSymbolContentMode | null => {
  if (profile.representation !== 'symbol') {
    return null;
  }
  if (stackRole === 'overlay_label') {
    return 'glyph';
  }
  const hasTextField = typeof profile.layout?.['text-field'] === 'string';
  const hasIconImage = Boolean(readSymbolIconToken(profile));
  if (hasTextField && !hasIconImage) {
    return 'glyph';
  }
  return 'sprite';
};

const resolveColorBindingCurrentValue = (
  profile: MapExperienceStyleProfile,
  bindingId: MapWorkbenchRenderStackColorBindingId,
): MapExperienceStyleColorValue | null => {
  if (bindingId === 'primary') {
    if (profile.kind === 'single') {
      return profile.color;
    }
    return null;
  }
  const paintProperty = getPaintPropertyForBinding(bindingId, profile.representation ?? null);
  if (!paintProperty || profile.kind !== 'single') {
    return null;
  }
  return normalizeOptionalMapExperienceColorValue(profile.paint?.[paintProperty]);
};

const getRoleAwareColorBindings = (
  stackRole: MapExperienceRenderStackRole,
  profile: MapExperienceStyleProfile,
  options?: {
    symbolIconTintable?: boolean;
    symbolContentMode?: MapWorkbenchRenderStackSymbolContentMode | null;
  },
  themeColors?: SemanticThemeColorPalette | null,
): MapWorkbenchRenderStackColorBindingDescriptor[] => {
  const semanticOptions = buildSemanticColorOptions(themeColors);
  const withCurrentValue = (
    binding: Omit<MapWorkbenchRenderStackColorBindingDescriptor, 'current_value' | 'resolved_preview_color'>,
  ): MapWorkbenchRenderStackColorBindingDescriptor => {
    const currentValue = resolveColorBindingCurrentValue(profile, binding.id);
    return {
      ...binding,
      current_value: currentValue,
      resolved_preview_color: currentValue ? resolveMapExperienceColorValue(currentValue, themeColors, '') : null,
    };
  };
  const isSymbolRepresentation = profile.representation === 'symbol';
  const symbolContentMode = options?.symbolContentMode ?? null;
  switch (stackRole) {
    case 'overlay_badge':
      if (isSymbolRepresentation) {
        if (symbolContentMode === 'glyph') {
          return [
            withCurrentValue({
              id: 'primary',
              label: 'Text / Glyph Color',
              description: 'Primary badge glyph or text color.',
              state: 'available',
              supports_literal_color: true,
              recommended_token: 'primary',
              semantic_options: semanticOptions,
            }),
            withCurrentValue({
              id: 'halo',
              label: 'Text / Glyph Halo',
              description: 'Contrast halo behind badge glyph or text content.',
              state: 'available',
              supports_literal_color: true,
              recommended_token: 'map-background',
              semantic_options: semanticOptions,
            }),
          ];
        }
        return options?.symbolIconTintable
          ? [
              withCurrentValue({
                id: 'icon',
                label: 'Icon Color',
                description: 'Tint color for the selected SDF-backed badge icon.',
                state: 'available',
                supports_literal_color: true,
                recommended_token: 'primary',
                semantic_options: semanticOptions,
              }),
              withCurrentValue({
                id: 'icon_halo',
                label: 'Icon Halo',
                description: 'Halo color for the selected SDF-backed badge icon.',
                state: 'available',
                supports_literal_color: true,
                recommended_token: 'map-background',
                semantic_options: semanticOptions,
              }),
            ]
          : [];
      }
      return [
        withCurrentValue({
          id: 'primary',
          label: 'Badge Color',
          description: 'Primary badge fill or glyph color.',
          state: 'available',
          supports_literal_color: true,
          recommended_token: 'primary',
          semantic_options: semanticOptions,
        }),
      ];
    case 'overlay_label':
      return [
        withCurrentValue({
          id: 'primary',
          label: 'Text Color',
          description: 'Primary label text color; map-native label tokens are preferred for readability.',
          state: 'available',
          supports_literal_color: true,
          recommended_token: 'map-label',
          semantic_options: semanticOptions,
        }),
        withCurrentValue({
          id: 'halo',
          label: 'Text Halo',
          description: 'Contrast halo behind label text; map-background is the preferred token.',
          state: 'available',
          supports_literal_color: true,
          recommended_token: 'map-background',
          semantic_options: semanticOptions,
        }),
      ];
    case 'overlay_symbol':
      if (symbolContentMode === 'glyph') {
        return [
          withCurrentValue({
            id: 'primary',
            label: 'Text / Glyph Color',
            description: 'Text or glyph paint color for symbol overlays.',
            state: 'available',
            supports_literal_color: true,
            recommended_token: 'map-label',
            semantic_options: semanticOptions,
          }),
          withCurrentValue({
            id: 'halo',
            label: 'Text / Glyph Halo',
            description: 'Contrast halo behind symbol text or glyph content.',
            state: 'available',
            supports_literal_color: true,
            recommended_token: 'map-background',
            semantic_options: semanticOptions,
          }),
        ];
      }
      return options?.symbolIconTintable
        ? [
            withCurrentValue({
              id: 'icon',
              label: 'Icon Color',
              description: 'Tint color for the selected SDF-backed icon.',
              state: 'available',
              supports_literal_color: true,
              recommended_token: 'primary',
              semantic_options: semanticOptions,
            }),
            withCurrentValue({
              id: 'icon_halo',
              label: 'Icon Halo',
              description: 'Halo color for the selected SDF-backed icon.',
              state: 'available',
              supports_literal_color: true,
              recommended_token: 'map-background',
              semantic_options: semanticOptions,
            }),
          ]
        : [];
    case 'base':
    default:
      if (isSymbolRepresentation) {
        if (symbolContentMode === 'glyph') {
          return [
            withCurrentValue({
              id: 'primary',
              label: 'Text / Glyph Color',
              description: 'Main authored text or glyph color for the active symbol treatment.',
              state: 'available',
              supports_literal_color: true,
              recommended_token: 'map-label',
              semantic_options: semanticOptions,
            }),
            withCurrentValue({
              id: 'halo',
              label: 'Text / Glyph Halo',
              description: 'Contrast halo behind symbol text or glyph content.',
              state: 'available',
              supports_literal_color: true,
              recommended_token: 'map-background',
              semantic_options: semanticOptions,
            }),
          ];
        }
        return options?.symbolIconTintable
          ? [
              withCurrentValue({
                id: 'icon',
                label: 'Icon Color',
                description: 'Tint color for the selected SDF-backed icon.',
                state: 'available',
                supports_literal_color: true,
                recommended_token: 'primary',
                semantic_options: semanticOptions,
              }),
              withCurrentValue({
                id: 'icon_halo',
                label: 'Icon Halo',
                description: 'Halo color for the selected SDF-backed icon.',
                state: 'available',
                supports_literal_color: true,
                recommended_token: 'map-background',
                semantic_options: semanticOptions,
              }),
            ]
          : [];
      }
      return [
        withCurrentValue({
          id: 'primary',
          label: isSymbolRepresentation ? 'Text / Glyph Color' : 'Primary Color',
          description: isSymbolRepresentation
            ? 'Main authored text or glyph color for the active symbol treatment.'
            : 'Main authored color for the active representation.',
          state: 'available',
          supports_literal_color: true,
          recommended_token: isSymbolRepresentation ? 'map-label' : 'primary',
          semantic_options: semanticOptions,
        }),
        withCurrentValue({
          id: 'stroke',
          label: 'Stroke / Outline',
          description: 'Outline or border color when the active representation supports it.',
          state: 'available',
          supports_literal_color: true,
          recommended_token: 'map-border',
          semantic_options: semanticOptions,
        }),
        withCurrentValue({
          id: 'halo',
          label: isSymbolRepresentation ? 'Text / Glyph Halo' : 'Text / Symbol Halo',
          description: isSymbolRepresentation
            ? 'Contrast halo behind symbol text or glyph content.'
            : 'Contrast halo for text or symbol treatments.',
          state: 'available',
          supports_literal_color: true,
          recommended_token: 'map-background',
          semantic_options: semanticOptions,
        }),
      ];
  }
};

const getRoleAwareLayoutControls = (
  stackRole: MapExperienceRenderStackRole,
  profile: MapExperienceStyleProfile,
  symbolContentMode?: MapWorkbenchRenderStackSymbolContentMode | null,
  options?: {
    targetHasBadgeOverlay?: boolean;
  },
): MapWorkbenchRenderStackLayoutControlDescriptor[] => {
  const layout = profile.layout ?? null;
  const paint = profile.kind === 'single' ? profile.paint ?? null : null;
  const baseOverlapRequired = stackRole === 'base' && options?.targetHasBadgeOverlay === true;
  const withRequiredSuffix = (description: string) =>
    baseOverlapRequired ? `${description} Required when badge overlays exist for this target.` : description;
  const symbolDeclutterControls =
    profile.representation === 'symbol'
      ? ([
          {
            target: 'layout',
            id: 'icon_allow_overlap',
            prop: 'icon-allow-overlap',
            label: 'Icon Allow Overlap',
            description: withRequiredSuffix('Allow icons to render even when they collide with other symbols.'),
            state: 'available',
            input: 'boolean',
            current_value: typeof layout?.['icon-allow-overlap'] === 'boolean' ? layout['icon-allow-overlap'] : undefined,
          },
          {
            target: 'layout',
            id: 'icon_ignore_placement',
            prop: 'icon-ignore-placement',
            label: 'Icon Ignore Placement',
            description: 'Let icons ignore placement rules from nearby symbols.',
            state: 'available',
            input: 'boolean',
            current_value: typeof layout?.['icon-ignore-placement'] === 'boolean' ? layout['icon-ignore-placement'] : undefined,
          },
          {
            target: 'layout',
            id: 'text_allow_overlap',
            prop: 'text-allow-overlap',
            label: 'Text Allow Overlap',
            description: withRequiredSuffix('Allow text or glyph content to render even when it collides with other symbols.'),
            state: 'available',
            input: 'boolean',
            current_value: typeof layout?.['text-allow-overlap'] === 'boolean' ? layout['text-allow-overlap'] : undefined,
          },
          {
            target: 'layout',
            id: 'text_ignore_placement',
            prop: 'text-ignore-placement',
            label: 'Text Ignore Placement',
            description: 'Let text or glyph content ignore placement rules from nearby symbols.',
            state: 'available',
            input: 'boolean',
            current_value: typeof layout?.['text-ignore-placement'] === 'boolean' ? layout['text-ignore-placement'] : undefined,
          },
        ] satisfies MapWorkbenchRenderStackLayoutControlDescriptor[])
      : [];
  const iconDeclutterControls = symbolDeclutterControls.filter(control => control.prop.startsWith('icon-'));
  const textDeclutterControls = symbolDeclutterControls.filter(control => control.prop.startsWith('text-'));
  if (stackRole === 'overlay_badge') {
    if (profile.representation === 'circle') {
      return [
        {
          target: 'paint',
          id: 'circle_radius',
          prop: 'circle-radius',
          label: 'Badge Radius',
          description: 'Scale the circular badge relative to the anchored point feature.',
          state: 'available',
          input: 'number',
          current_value: typeof paint?.['circle-radius'] === 'number' ? paint['circle-radius'] : undefined,
          min: 0,
          step: 0.5,
        },
      ];
    }
    if (profile.representation === 'symbol') {
      if (symbolContentMode === 'glyph') {
        return [
          {
            target: 'layout',
            id: 'text_size',
            prop: 'text-size',
            label: 'Badge Text Size',
            description: 'Scale the badge text relative to the anchored point feature.',
            state: 'available',
            input: 'number',
            current_value: typeof layout?.['text-size'] === 'number' ? layout['text-size'] : undefined,
            min: 0,
            step: 1,
          },
          {
            target: 'layout',
            id: 'text_anchor',
            prop: 'text-anchor',
            label: 'Badge Anchor',
            description: 'Choose which side of the anchored point feature the badge text should prefer.',
            state: 'available',
            input: 'select',
            current_value: typeof layout?.['text-anchor'] === 'string' ? layout['text-anchor'] : undefined,
            options: TEXT_ANCHOR_OPTIONS,
          },
          {
            target: 'layout',
            id: 'text_offset',
            prop: 'text-offset',
            label: 'Badge Offset',
            description: 'Apply an x,y offset so the badge text sits away from the anchored point feature.',
            state: 'available',
            input: 'number-list',
            current_value: Array.isArray(layout?.['text-offset']) ? layout['text-offset'] : undefined,
            placeholder: 'x, y',
          },
          {
            target: 'layout',
            id: 'text_radial_offset',
            prop: 'text-radial-offset',
            label: 'Badge Radial Offset',
            description: 'Push the badge text outward from the anchored point feature without managing both x and y.',
            state: 'available',
            input: 'number',
            current_value:
              typeof layout?.['text-radial-offset'] === 'number' ? layout['text-radial-offset'] : undefined,
            step: 0.1,
          },
          ...textDeclutterControls,
        ];
      }
      return [
        {
          target: 'layout',
          id: 'icon_size',
          prop: 'icon-size',
          label: 'Badge Icon Size',
          description: 'Scale the badge icon relative to the anchored point feature.',
          state: 'available',
          input: 'number',
          current_value: typeof layout?.['icon-size'] === 'number' ? layout['icon-size'] : undefined,
          min: 0,
          step: 0.1,
        },
        {
          target: 'layout',
          id: 'icon_offset',
          prop: 'icon-offset',
          label: 'Badge Icon Offset',
          description: 'Move the badge icon away from the anchored point feature without changing the base symbol.',
          state: 'available',
          input: 'number-list',
          current_value: Array.isArray(layout?.['icon-offset']) ? layout['icon-offset'] : undefined,
          placeholder: 'x, y',
          step: 0.1,
        },
        ...iconDeclutterControls,
      ];
    }
  }
  if (stackRole !== 'overlay_label') {
    if (profile.representation !== 'symbol') {
      return symbolDeclutterControls;
    }
    return symbolContentMode === 'glyph' ? textDeclutterControls : iconDeclutterControls;
  }
  return [
    {
      target: 'layout',
      id: 'text_anchor',
      prop: 'text-anchor',
      label: 'Anchor',
      description: 'Choose the anchor position used when placing the label relative to the feature.',
      state: 'available',
      input: 'select',
      current_value: typeof layout?.['text-anchor'] === 'string' ? layout['text-anchor'] : undefined,
      options: TEXT_ANCHOR_OPTIONS,
    },
    {
      target: 'layout',
      id: 'text_offset',
      prop: 'text-offset',
      label: 'Offset',
      description: 'Apply a manual x,y offset so the label sits away from the base marker or icon.',
      state: 'available',
      input: 'number-list',
      current_value: Array.isArray(layout?.['text-offset']) ? layout['text-offset'] : undefined,
      placeholder: 'x, y',
    },
    {
      target: 'layout',
      id: 'text_radial_offset',
      prop: 'text-radial-offset',
      label: 'Radial Offset',
      description: 'Push the label outward from the anchor point without manually managing both x and y.',
      state: 'available',
      input: 'number',
      current_value:
        typeof layout?.['text-radial-offset'] === 'number' ? layout['text-radial-offset'] : undefined,
      step: 0.1,
    },
    ...textDeclutterControls,
  ];
};

const getRoleAwareTextBindingControls = (
  stackRole: MapExperienceRenderStackRole,
  profile: MapExperienceStyleProfile,
  symbolContentMode?: MapWorkbenchRenderStackSymbolContentMode | null,
): MapWorkbenchRenderStackTextBindingControlDescriptor[] => {
  if (stackRole === 'overlay_label') {
    return [
      {
        id: 'text_field',
        prop: 'text-field',
        label: 'Label Text',
        description: 'Enter authored label text or a field token such as {name}.',
        state: 'available',
        current_value: typeof profile.layout?.['text-field'] === 'string' ? profile.layout['text-field'] : null,
        placeholder: '{name} or custom label',
      },
    ];
  }
  if (profile.representation !== 'symbol' || symbolContentMode !== 'glyph') {
    return [];
  }
  return [
    {
      id: 'text_field',
      prop: 'text-field',
      label: stackRole === 'overlay_badge' ? 'Badge Text' : 'Glyph / Text',
      description:
        stackRole === 'overlay_badge'
          ? 'Enter authored badge text or a field token such as {status}.'
          : 'Enter authored text or a field token such as {name}.',
      state: 'available',
      current_value: typeof profile.layout?.['text-field'] === 'string' ? profile.layout['text-field'] : null,
      placeholder: stackRole === 'overlay_badge' ? '✓ or {status}' : '{name} or custom glyph',
    },
  ];
};

const buildEntryEditorViewModel = (
  entry: MapWorkbenchRenderStackEntryViewModel,
  profile: MapExperienceStyleProfile,
  geometry: MapWorkbenchRenderStackGeometryKind,
  spriteIconSdfByToken?: Record<string, boolean> | null,
  themeColors?: SemanticThemeColorPalette | null,
  options?: {
    targetHasBadgeOverlay?: boolean;
  },
): MapWorkbenchRenderStackEntryEditorViewModel => {
  const roleLabel = STACK_ROLE_LABELS[entry.stack_role];
  const iconToken = readSymbolIconToken(profile);
  const symbolIconTintable = Boolean(iconToken && spriteIconSdfByToken?.[iconToken]);
  const symbolContentMode = resolveSymbolContentMode(entry.stack_role, profile);
  switch (entry.stack_role) {
    case 'overlay_badge':
      return {
        entry_id: entry.profile_id,
        profile_id: entry.profile_id,
        stack_role: entry.stack_role,
        role_label: roleLabel,
        title: `Edit Badge Overlay: ${entry.label}`,
        description: 'Configure a compact overlay badge that decorates matching features without replacing the base style.',
        symbol_content_mode: symbolContentMode,
        strategy_options: getRoleAwareStrategyOptions(entry.stack_role),
        representation_options: getRoleAwareRepresentationOptions(entry.stack_role, geometry, entry.representation),
        control_support: getRoleAwareControlSupport(entry.stack_role, {
          targetHasBadgeOverlay: options?.targetHasBadgeOverlay,
          representation: entry.representation,
        }),
        text_binding_controls: getRoleAwareTextBindingControls(entry.stack_role, profile, symbolContentMode),
        color_bindings: getRoleAwareColorBindings(
          entry.stack_role,
          profile,
          { symbolIconTintable, symbolContentMode },
          themeColors,
        ),
        layout_controls: getRoleAwareLayoutControls(entry.stack_role, profile, symbolContentMode, {
          targetHasBadgeOverlay: options?.targetHasBadgeOverlay,
        }),
      };
    case 'overlay_label':
      return {
        entry_id: entry.profile_id,
        profile_id: entry.profile_id,
        stack_role: entry.stack_role,
        role_label: roleLabel,
        title: `Edit Label Overlay: ${entry.label}`,
        description: 'Configure a text-first overlay for feature labels. The shared editor keeps this overlay on symbol-based controls.',
        symbol_content_mode: symbolContentMode,
        strategy_options: getRoleAwareStrategyOptions(entry.stack_role),
        representation_options: getRoleAwareRepresentationOptions(entry.stack_role, geometry, entry.representation),
        control_support: getRoleAwareControlSupport(entry.stack_role, {
          targetHasBadgeOverlay: options?.targetHasBadgeOverlay,
          representation: entry.representation,
        }),
        text_binding_controls: getRoleAwareTextBindingControls(entry.stack_role, profile, symbolContentMode),
        color_bindings: getRoleAwareColorBindings(
          entry.stack_role,
          profile,
          { symbolIconTintable, symbolContentMode },
          themeColors,
        ),
        layout_controls: getRoleAwareLayoutControls(entry.stack_role, profile, symbolContentMode, {
          targetHasBadgeOverlay: options?.targetHasBadgeOverlay,
        }),
      };
    case 'overlay_symbol':
      return {
        entry_id: entry.profile_id,
        profile_id: entry.profile_id,
        stack_role: entry.stack_role,
        role_label: roleLabel,
        title: `Edit Symbol Overlay: ${entry.label}`,
        description: 'Configure an icon or symbol overlay that sits above the base style and can be filtered independently.',
        symbol_content_mode: symbolContentMode,
        strategy_options: getRoleAwareStrategyOptions(entry.stack_role),
        representation_options: getRoleAwareRepresentationOptions(entry.stack_role, geometry, entry.representation),
        control_support: getRoleAwareControlSupport(entry.stack_role, {
          targetHasBadgeOverlay: options?.targetHasBadgeOverlay,
          representation: entry.representation,
        }),
        text_binding_controls: getRoleAwareTextBindingControls(entry.stack_role, profile, symbolContentMode),
        color_bindings: getRoleAwareColorBindings(
          entry.stack_role,
          profile,
          { symbolIconTintable, symbolContentMode },
          themeColors,
        ),
        layout_controls: getRoleAwareLayoutControls(entry.stack_role, profile, symbolContentMode, {
          targetHasBadgeOverlay: options?.targetHasBadgeOverlay,
        }),
      };
    case 'base':
    default:
      return {
        entry_id: entry.profile_id,
        profile_id: entry.profile_id,
        stack_role: entry.stack_role,
        role_label: roleLabel,
        title: `Edit Base Style: ${entry.label}`,
        description: options?.targetHasBadgeOverlay && entry.representation === 'symbol'
          ? 'Configure the primary visual treatment for this rendered layer. This symbol also anchors badge overlays, so overlap must remain enabled while those badges exist.'
          : 'Configure the primary visual treatment for this rendered layer.',
        symbol_content_mode: symbolContentMode,
        strategy_options: getRoleAwareStrategyOptions(entry.stack_role),
        representation_options: getRoleAwareRepresentationOptions(entry.stack_role, geometry, entry.representation),
        control_support: getRoleAwareControlSupport(entry.stack_role, {
          targetHasBadgeOverlay: options?.targetHasBadgeOverlay,
          representation: entry.representation,
        }),
        text_binding_controls: getRoleAwareTextBindingControls(entry.stack_role, profile, symbolContentMode),
        color_bindings: getRoleAwareColorBindings(
          entry.stack_role,
          profile,
          { symbolIconTintable, symbolContentMode },
          themeColors,
        ),
        layout_controls: getRoleAwareLayoutControls(entry.stack_role, profile, symbolContentMode, {
          targetHasBadgeOverlay: options?.targetHasBadgeOverlay,
        }),
      };
  }
};

export const normalizeMapWorkbenchRenderStackTemplateId = (
  value: string,
): MapWorkbenchRenderStackTemplateId | null => {
  switch (value) {
    case 'badge':
    case 'overlay_badge':
      return 'badge';
    case 'label':
    case 'overlay_label':
      return 'label';
    case 'symbol_overlay':
    case 'overlay_symbol':
      return 'symbol_overlay';
    case 'duplicate_base_as_overlay':
      return 'duplicate_base_as_overlay';
    default:
      return null;
  }
};

const describeFilterLogic = (logic: StyleRuleLogic | null | undefined): string | null => {
  if (!logic) {
    return null;
  }
  if (logic.type === 'condition') {
    if (logic.operatorId === 'match_everything') {
      return 'Matches everything';
    }
    const field = logic.fieldId || 'Field';
    const value = Array.isArray(logic.values) && logic.values.length > 0
      ? logic.values.join(', ')
      : null;
    switch (logic.operatorId) {
      case 'eq':
        return value ? `${field} equals ${value}` : `${field} equals`;
      case 'neq':
        return value ? `${field} does not equal ${value}` : `${field} does not equal`;
      case 'contains':
        return value ? `${field} contains ${value}` : `${field} contains`;
      case 'exists':
        return `${field} exists`;
      case 'gt':
        return value ? `${field} > ${value}` : `${field} >`;
      case 'gte':
        return value ? `${field} >= ${value}` : `${field} >=`;
      case 'lt':
        return value ? `${field} < ${value}` : `${field} <`;
      case 'lte':
        return value ? `${field} <= ${value}` : `${field} <=`;
      case 'in':
        return value ? `${field} in ${value}` : `${field} in list`;
      default:
        return field;
    }
  }
  return logic.operator === 'OR' ? 'Match any of these conditions' : 'Match all of these conditions';
};

const resolveProfileSwatchColor = (
  profile: MapExperienceStyleProfile,
  themeColors?: SemanticThemeColorPalette | null,
): string | null => {
  if (profile.kind === 'single') {
    return resolveMapExperienceColorValue(profile.color, themeColors);
  }
  if (profile.kind === 'categorical') {
    const color = resolveMapExperienceColorValue(profile.categories[0]?.color ?? profile.fallback_color ?? null, themeColors, '');
    return color || null;
  }
  if (profile.kind === 'range') {
    const color = resolveMapExperienceColorValue(profile.breaks[0]?.color ?? profile.fallback_color ?? null, themeColors, '');
    return color || null;
  }
  const color = resolveMapExperienceColorValue(profile.rules?.find(rule => rule.enabled)?.color ?? null, themeColors, '');
  return color || null;
};

export const buildMapWorkbenchRenderStackController = ({
  experience,
  target,
  spriteIconSdfByToken,
  themeColors,
  readOnly,
  selectedEntryId,
  selectedProfileId,
  onSelectEntry,
  onAddOverlay,
  onMoveEntry,
  onDuplicateEntry,
  onDeleteEntry,
  onToggleEntryEnabled,
  onEditEntry,
}: BuildMapWorkbenchRenderStackControllerArgs): MapWorkbenchRenderStackControllerViewModel => {
  const resolvedEntries = resolveMapExperienceRenderStackEntries({
    experience,
    sourceId: target.sourceId,
    sourceLayer: target.sourceLayer,
  });
  const entryViewModels = resolvedEntries.map((entry, index) => ({
    id: entry.entryId,
    label: entry.label?.trim() || (entry.stackRole === 'base' ? 'Base' : `Overlay ${index}`),
    enabled: entry.enabled,
    synthetic: entry.synthetic,
    stack_role: entry.stackRole,
    role_label: STACK_ROLE_LABELS[entry.stackRole],
    profile_id: entry.profile.id,
    profile_kind: entry.profile.kind,
    representation: entry.profile.representation ?? null,
    swatch_color: resolveProfileSwatchColor(entry.profile, themeColors),
    filter_logic: entry.filterLogic,
    filter_summary: describeFilterLogic(entry.filterLogic),
    position: index,
    is_base: entry.stackRole === 'base',
    can_move_up: index > 0,
    can_move_down: index < resolvedEntries.length - 1,
  }));
  const selectedId =
    entryViewModels.find(entry => entry.id === selectedEntryId)?.id
    ?? entryViewModels.find(entry => entry.profile_id === selectedProfileId)?.id
    ?? entryViewModels[0]?.id
    ?? null;
  const selectedEntry = entryViewModels.find(entry => entry.id === selectedId) ?? null;
  const selectedResolvedEntry = resolvedEntries.find(entry => entry.entryId === selectedId) ?? null;
  const targetHasBadgeOverlay = resolvedEntries.some(entry => entry.enabled && entry.stackRole === 'overlay_badge');
  const geometry = target.geometry ?? 'unknown';

  return {
    target_key: buildMapExperienceTargetKey(target.sourceId, target.sourceLayer),
    selected_entry_id: selectedId,
    selected_entry: selectedEntry,
    selected_entry_editor:
      selectedEntry && selectedResolvedEntry
        ? buildEntryEditorViewModel(
            selectedEntry,
            selectedResolvedEntry.profile,
            geometry,
            spriteIconSdfByToken,
            themeColors,
            { targetHasBadgeOverlay },
          )
        : null,
    read_only: readOnly === true,
    entries: entryViewModels.map((entry, index) => ({
      ...entry,
      can_move_down: index < entryViewModels.length - 1,
    })),
    template_options: RENDER_STACK_TEMPLATE_OPTIONS,
    can_add_overlay: readOnly !== true,
    onSelectEntry,
    onAddOverlay,
    onMoveEntry,
    onDuplicateEntry,
    onDeleteEntry,
    onToggleEntryEnabled,
    onEditEntry,
  };
};
